import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import api from '../services/api';

export default function ReviewLinkSettings({ onSave }) {
  const [linkData, setLinkData] = useState(null);
  const [googleUrl, setGoogleUrl] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [businessCategory, setBusinessCategory] = useState('local_business');

  useEffect(() => {
    // Fetch business category
    api.getBusinesses().then(businesses => {
      if (businesses.length > 0) {
        setBusinessCategory(businesses[0].business_category || 'local_business');
      }
    }).catch(() => {});

    const fetchLink = async () => {
      try {
        const data = await api.get('/review-link');
        if (data) {
          setLinkData(data);
          setGoogleUrl(data.google_review_url || '');
          setCustomSlug(data.slug || '');
        }
      } catch { /* not set up yet */ }
    };
    fetchLink();
  }, []);

  const isHospitality = businessCategory === 'hospitality';

  // Generate QR code whenever linkData changes
  useEffect(() => {
    if (linkData?.full_link) {
      QRCode.toDataURL(linkData.full_link, {
        width: 512,
        margin: 2,
        color: { dark: '#0b1628', light: '#ffffff' },
        errorCorrectionLevel: 'H',
      }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
    }
  }, [linkData]);

  const handleSave = async () => {
    if (!googleUrl.trim()) {
      setError(isHospitality
        ? 'Please enter your Airbnb listing URL'
        : 'Please enter your Google review URL');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const data = await api.post('/review-link', {
        google_review_url: googleUrl.trim(),
        custom_slug: customSlug.trim() || null,
      });
      setLinkData(data);
      setSuccess('Review link saved!');
      setTimeout(() => setSuccess(''), 3000);
      onSave?.();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save review link');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (linkData?.full_link) {
      navigator.clipboard.writeText(linkData.full_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `stellarreply-qr-${linkData?.slug || 'review-link'}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadPrintCard = () => {
    if (!qrDataUrl) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 600;
    canvas.height = 720;

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 600, 720);

    // Header bar
    ctx.fillStyle = '#0b1628';
    ctx.fillRect(0, 0, 600, 60);
    ctx.fillStyle = '#34d399';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('StellarReply', 300, 38);

    // Title
    ctx.fillStyle = '#0b1628';
    ctx.font = 'bold 28px sans-serif';
    ctx.fillText(
      isHospitality ? 'How was your stay?' : 'Scan to leave us a review!',
      300, 110
    );

    // Star rating decoration
    ctx.font = '32px sans-serif';
    ctx.fillText('\u2B50\u2B50\u2B50\u2B50\u2B50', 300, 155);

    // QR code
    const qrImg = new Image();
    qrImg.onload = () => {
      ctx.drawImage(qrImg, 125, 180, 350, 350);

      // Link text
      ctx.fillStyle = '#666666';
      ctx.font = '14px sans-serif';
      ctx.fillText(linkData?.full_link || '', 300, 565);

      // Footer text
      ctx.fillStyle = '#999999';
      ctx.font = '13px sans-serif';
      ctx.fillText(
        isHospitality ? 'Your feedback helps us be better hosts' : 'Your feedback helps us improve',
        300, 610
      );
      ctx.fillText('Thank you for your support!', 300, 635);

      // Bottom bar
      ctx.fillStyle = '#0b1628';
      ctx.fillRect(0, 670, 600, 50);
      ctx.fillStyle = '#34d399';
      ctx.font = '12px sans-serif';
      ctx.fillText('Powered by stellarreply.com', 300, 700);

      // Download
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `stellarreply-qr-card-${linkData?.slug || 'review'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };
    qrImg.src = qrDataUrl;
  };

  return (
    <div>
      <hr className="settings-divider" />

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
        {isHospitality ? 'Guest Feedback Link' : 'Review Request Link'}
      </h2>
      <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 20 }}>
        {isHospitality
          ? 'Generate a short branded link to share with guests. Happy guests can leave a testimonial, unhappy guests get routed to your private feedback form and recovery loop.'
          : "Generate a short branded link to share with customers. When they click it, they'll go straight to your Google review page."}
      </p>

      {/* Current link display */}
      {linkData && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          background: 'rgba(52, 211, 153, 0.06)',
          border: '1px solid rgba(52, 211, 153, 0.2)',
          borderRadius: 8,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 600, fontFamily: 'monospace', color: 'var(--text)', minWidth: 200 }}>
            {linkData.full_link}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={copyLink}>
              {copied ? '\u2713 Copied' : 'Copy'}
            </button>
            {qrDataUrl && (
              <button
                className="btn btn-sm"
                onClick={() => setShowQr(!showQr)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  color: 'var(--text)',
                }}
              >
                {showQr ? 'Hide QR' : 'QR Code'}
              </button>
            )}
            <span style={{ fontSize: 12, color: 'var(--text-light)', whiteSpace: 'nowrap' }}>
              {linkData.clicks} click{linkData.clicks !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {/* QR Code display */}
      {showQr && qrDataUrl && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 24,
          background: '#ffffff',
          border: '1px solid var(--border)',
          borderRadius: 12,
          marginBottom: 20,
        }}>
          <img
            src={qrDataUrl}
            alt="Review link QR code"
            style={{ width: 200, height: 200, marginBottom: 12 }}
          />
          <p style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600 }}>
            {isHospitality ? 'Scan to rate your stay' : 'Scan to leave a review'}
          </p>
          <p style={{ fontSize: 11, color: '#999', marginBottom: 16 }}>
            {linkData?.full_link}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={downloadQr}>
              ↓ Download PNG
            </button>
            <button
              className="btn btn-sm"
              onClick={downloadPrintCard}
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'inherit',
                color: 'var(--text)',
              }}
            >
              ↓ Download Print Card
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#999', marginTop: 12 }}>
            {isHospitality
              ? 'Print card includes branding — perfect for welcome books, fridge magnets, and checkout cards'
              : 'Print card includes branding — perfect for table cards, receipts, and welcome packs'}
          </p>
        </div>
      )}

      {/* URL input */}
      <div className="settings-field">
        <label>
          {isHospitality ? 'Airbnb Listing URL' : 'Google Review URL'}
          <button
            onClick={() => setShowHelp(!showHelp)}
            style={{
              background: 'none', border: 'none', color: 'var(--accent)',
              fontSize: 12, cursor: 'pointer', marginLeft: 8, fontWeight: 600,
            }}
          >
            {showHelp ? 'Hide help' : 'How to find this?'}
          </button>
        </label>
        {showHelp && (
          <div style={{
            padding: '12px 16px',
            background: 'var(--bg)',
            borderRadius: 8,
            fontSize: 13,
            color: 'var(--text-light)',
            marginBottom: 12,
            lineHeight: 1.6,
          }}>
            {isHospitality ? (
              <>
                <strong style={{ color: 'var(--text)' }}>To find your Airbnb listing URL:</strong><br />
                1. Go to your Airbnb hosting dashboard<br />
                2. Click on your listing<br />
                3. Click "Preview listing" to see the public view<br />
                4. Copy the full URL from your browser's address bar<br /><br />
                Accepted URL formats:<br />
                • https://www.airbnb.co.uk/rooms/12345<br />
                • https://www.airbnb.com/rooms/12345
              </>
            ) : (
              <>
                <strong style={{ color: 'var(--text)' }}>To find your Google review URL:</strong><br />
                1. Search for your business on Google Maps<br />
                2. Click your business listing<br />
                3. Click "Write a review"<br />
                4. Copy the full URL from your browser's address bar<br /><br />
                Accepted URL formats:<br />
                • Google Maps URLs (https://www.google.com/maps/place/...)<br />
                • Google short links (https://g.page/...)<br />
                • Direct review links (https://search.google.com/local/writereview?placeid=...)
              </>
            )}
          </div>
        )}
        <input
          type="url"
          value={googleUrl}
          onChange={(e) => setGoogleUrl(e.target.value)}
          placeholder={isHospitality
            ? 'https://www.airbnb.co.uk/rooms/your-listing-id'
            : 'https://www.google.com/maps/place/your-business...'}
        />
        <span className="settings-hint">
          {isHospitality
            ? 'Paste your Airbnb listing URL. This powers the feedback funnel, Listing Intelligence, and competitor comparison.'
            : 'Paste your Google Maps review link or Google Place review URL'}
        </span>
      </div>

      {/* Custom slug */}
      <div className="settings-field">
        <label>Custom Link Slug (optional)</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <span style={{
            padding: '10px 12px',
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRight: 'none',
            borderRadius: '8px 0 0 8px',
            fontSize: 13,
            color: 'var(--text-light)',
            whiteSpace: 'nowrap',
          }}>
            stellarreply.com/r/
          </span>
          <input
            type="text"
            value={customSlug}
            onChange={(e) => setCustomSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder={isHospitality ? 'your-property-name' : 'your-business-name'}
            style={{ borderRadius: '0 8px 8px 0' }}
          />
        </div>
        <span className="settings-hint">
          {isHospitality
            ? 'Leave blank to auto-generate from your property name'
            : 'Leave blank to auto-generate from your business name'}
        </span>
      </div>

      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div style={{ color: 'var(--green)', fontSize: 13, fontWeight: 500, marginBottom: 12 }}>{success}</div>}

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : linkData ? 'Update Link' : 'Generate Link'}
      </button>

      {/* Usage tips */}
      {linkData && (
        <div style={{ marginTop: 24, padding: 16, background: 'var(--bg)', borderRadius: 8 }}>
          <strong style={{ fontSize: 13, color: 'var(--text)' }}>Where to use your link:</strong>
          <div style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 8, lineHeight: 1.8 }}>
            {isHospitality ? (
              <>
                📖 Welcome book or guest information folder<br />
                🧲 Fridge magnet or printed card in the property<br />
                📱 Post-checkout message or WhatsApp<br />
                📧 Follow-up email after their stay<br />
                🏡 Your Airbnb listing description ("See what guests say!")
              </>
            ) : (
              <>
                📧 Email signatures and follow-up emails<br />
                🧾 Printed on receipts and invoices<br />
                📱 QR code on table cards or welcome packs<br />
                💬 Post-purchase SMS or WhatsApp messages<br />
                🌐 Your website's footer or contact page
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
